import { Routes, RouterModule } from '@angular/router';
import { ModuleWithProviders } from '@angular/core';
import {
  LoginAuthGuard,
  PermissionGuard,
  PermissionTypes,
  PermissionAppGuard,
  RedirectToLatestContextGuard,
  SetLatestContextGuard,
  buildRemoteModule
} from '@arc/arc-shared-frontend';
import { SSOResultComponent } from './ssoresult/ssoresult.component';
import { Error401Component } from './errorpages/error401.component';
import { LandingComponent } from './settings/landing/landing.component';
import {
  UserProfileContainerComponent,
  UsersContainerComponent
} from '@arc/arc-user-frontend';

import {
  AccountListComponent,
  AccountPageComponent
} from '@arc/account-frontend';
import { applicationGuard } from '../app/navigation/guards/application.guard';
import { bpnAppGuard } from './navigation/guards/bpn-app.guard';
import { InvoicingCrossSellComponent } from './navigation/cross-sell/invoicing-cross-sell/invoicing-cross-sell.component';
import { PaymentsCrossSellComponent } from './navigation/cross-sell/payments-cross-sell/payments-cross-sell.component';
import { CashAppCrossSellComponent } from './navigation/cross-sell/cash-app-cross-sell/cash-app-cross-sell.component';
import { CollectionsCrossSellComponent } from './navigation/cross-sell/collections-cross-sell/collections-cross-sell.component';
import { CreditCrossSellComponent } from './navigation/cross-sell/credit-cross-sell/credit-cross-sell.component';
import { DisputesCrossSellComponent } from './navigation/cross-sell/disputes-cross-sell/disputes-cross-sell.component';

export const routes: Routes = [
  {
    path: 'invoicing',
    canMatch: [applicationGuard('invoicing')],
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'invoicingMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e => console.error(`Error loading Invoicing frontend: `, e))
  },
  {
    path: 'search',
    canActivate: [LoginAuthGuard, PermissionAppGuard],
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'invoicingMfe',
        exposedModule: './SearchApp',
        ngModuleName: 'ArcSearchModule'
      }).catch(e => console.error(`Error loading Search frontend: `, e))
  },
  {
    path: 'invoicing',
    redirectTo: 'invoicing-mktg-contact-sales'
  },
  {
    path: 'invoicing-mktg-contact-sales',
    component: InvoicingCrossSellComponent,
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'payments',
    canMatch: [applicationGuard('bpn-app')],
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'paymentMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e => console.error(`Error loading Payment frontend: `, e))
  },
  {
    path: 'bpn',
    redirectTo: 'payments'
  },
  {
    path: 'payments',
    redirectTo: 'payments-mktg-contact-sales'
  },
  {
    path: 'payments-mktg-contact-sales',
    component: PaymentsCrossSellComponent,
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'payment-accounts',
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'bpnAccountsMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e => console.error(`Error loading payment-accounts frontend: `, e))
  },
  {
    path: 'cash-app',
    canMatch: [applicationGuard('cash-app')],
    loadChildren: () =>
      import('./lazymodulewrappers/cashapp.lazy.module').then(
        m => m.CashappLazyModule
      )
  },
  {
    path: 'cash-app',
    redirectTo: 'cash-app-mktg-contact-sales'
  },
  {
    path: 'cash-app-mktg-contact-sales',
    component: CashAppCrossSellComponent,
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'collections',
    canMatch: [applicationGuard('icontroller-collections-app')],
    loadChildren: () =>
      import('./lazymodulewrappers/ic-collections.lazy.module').then(
        m => m.ICCollectionsLazyModule
      )
  },
  {
    path: 'collections',
    redirectTo: 'collections-mktg-contact-sales'
  },
  {
    path: 'collections-mktg-contact-sales',
    component: CollectionsCrossSellComponent,
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'credit-manager',
    canMatch: [applicationGuard('credit-app-2')],
    loadChildren: () =>
      import('./lazymodulewrappers/creditapp-manager.lazy.module').then(
        m => m.CreditAppManagerLazyModule
      )
  },
  {
    path: 'credit-manager',
    redirectTo: 'credit-manager-mktg-contact-sales'
  },
  {
    path: 'credit-manager-mktg-contact-sales',
    component: CreditCrossSellComponent,
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'disputes',
    canMatch: [applicationGuard('disputes-app')],
    loadChildren: () =>
      import('@arc/arc-dispute-frontend').then(
        m => m.ArcCollectionsDisputeModule
      )
  },
  {
    path: 'disputes',
    redirectTo: 'disputes-mktg-contact-sales'
  },
  {
    path: 'disputes-mktg-contact-sales',
    component: DisputesCrossSellComponent,
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'accounts',
    component: AccountListComponent,
    canActivate: [LoginAuthGuard, PermissionAppGuard],
    data: {
      permissionString:
        'arc-permission-section-accounts-permission-accountsmanagement:read',
      apps: ['credit-app-2', 'invoicing', 'collections-app', 'cash-app']
    }
  },
  {
    path: 'analytics',
    canMatch: [applicationGuard('analytics-app')],
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'analyticsMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e => console.error(`Error loading Analytics frontend: `, e))
  },
  {
    path: 'accounts/:accountGuid',
    canActivate: [LoginAuthGuard, PermissionAppGuard],
    data: {
      permissionString:
        'arc-permission-section-accounts-permission-accountsmanagement:read',
      apps: ['credit-app-2', 'invoicing', 'collections-app', 'cash-app']
    },
    component: AccountPageComponent,
    children: [
      {
        path: '',
        children: [],
        canActivate: [RedirectToLatestContextGuard],
        pathMatch: 'full'
      },
      {
        path: 'invoicing',
        loadChildren: () =>
          buildRemoteModule({
            mfeName: 'invoicingMfe',
            exposedModule: './InvoicingApp',
            ngModuleName: 'InvoicingModule'
          }).catch(e => console.error(`Error loading Invoicing frontend: `, e)),
        data: {
          tabId: 'invoicing',
          displayOrder: 0,
          labelKey: 'ARC_COMPOSITE_APPLICATION_TAB_INVOICING',
          apps: ['invoicing']
        },
        canActivate: [SetLatestContextGuard]
      },
      {
        path: 'credit',
        loadChildren: () =>
          import('./lazymodulewrappers/creditapp-account.lazy.module').then(
            m => m.CreditAppAccountLazyModule
          ),
        data: {
          tabId: 'credit-app-2',
          displayOrder: 2,
          labelKey: 'Credit',
          apps: ['credit-app-2']
        },
        canActivate: [SetLatestContextGuard]
      },
      { path: '**', redirectTo: '/accounts' }
    ]
  },
  { path: 'ssoresult', component: SSOResultComponent },
  { path: 'accessdenied', component: Error401Component },
  {
    path: 'system',
    canActivate: [LoginAuthGuard],
    component: LandingComponent,
  },
  {
    path: 'system/credit-admin',
    loadChildren: () =>
      import('./lazymodulewrappers/creditapp-admin.lazy.module').then(
        m => m.CreditAppAdminLazyModule
      ),
    canActivate: [LoginAuthGuard]
  },
  /* --------- Payments configuration UI --------- */
  {
    path: 'system/payments-settings',
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'paymentsConfigurationMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e =>
        console.error(`Error loading Payments configuration frontend: `, e)
      ),
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'system/iccollections-admin',
    loadChildren: () =>
      import('./lazymodulewrappers/ic-collections-admin.lazy.module').then(
        m => m.ICCollectionsAdminLazyModule
      ),
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'system/dispute-configuration',
    loadChildren: () =>
      import('@arc/arc-dispute-frontend').then(
        m => m.ArcCollectionsDisputeConfigurationModule
      ),
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'system/digital-lockbox-settings',
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'bpnSettingsMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e =>
        console.error(`Error loading digital-lockbox-settings frontend: `, e)
      )
  },
  {
    path: 'users',
    component: UsersContainerComponent,
    canActivate: [LoginAuthGuard, PermissionGuard],
    data: {
      permissionString: PermissionTypes.UserRoleManagement + ':read'
    },
    children: [{ path: 'profile', component: UserProfileContainerComponent }]
  },
  {
    path: 'profile',
    component: UserProfileContainerComponent,
    canActivate: [LoginAuthGuard]
  },

  // ---------------------Credit App Specific Credit Profile Route--------------
  {
    path: 'credit-profile/credit-report/company/:companyId',
    loadChildren: () =>
      import('./lazymodulewrappers/creditapp-reports.lazy.module').then(
        m => m.CreditAppReportLazyModule
      ),
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'tenants',
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'tenantMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e => console.error(`Error loading Tenant frontend: `, e))
  },
  {
    path: 'gpt-terminal',
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'gptMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e => console.error(`Error loading GPT frontend: `, e))
  },
  {
    path: 'bpni',
    loadChildren: () =>
      buildRemoteModule({
        mfeName: 'bpniPortalManagementMfe',
        exposedModule: './Module',
        ngModuleName: 'AppModule'
      }).catch(e => console.error(`Error loading BPNI frontend: `, e)),
    canActivate: [LoginAuthGuard]
  },
  {
    path: 'undefined',
    redirectTo: 'invoicing'
  },
  {
    path: '**',
    redirectTo: '/login'
  }
];

export const AppRoutes: ModuleWithProviders<any> = RouterModule.forRoot(
  routes,
  {
    onSameUrlNavigation: 'reload'
  }
);
